import * as React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Stack, Skeleton, Paper,
    Box, Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useEffect, useMemo, useState } from "react";
import { selectSettings } from "../settings/settingsSlice";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { selectModule } from "../modules/moduleSlice";
import { Device, loadDeviceDetails, loadDevices, selectAllDevices, selectDeviceById, selectDevices } from "./deviceSlice";
import { loadDeviceTypes, selectDeviceTypeById, selectDeviceTypes } from "./deviceTypesSlice";
import { selectCompanyById } from "../company/companiesSlice";
import { store } from "../../app/store";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

type DeviceSelectFieldProps = {
    value: string | null;
    onChange: (id: string) => void;
    onSelectDevice?: (device: Device | undefined) => void;
    disabled?: boolean;
};

export const DeviceSelectField: React.FC<DeviceSelectFieldProps> = ({
    value,
    onChange,
    onSelectDevice,
    disabled = false,
}) => {
    const dispatch = useAppDispatch();
    const { locale } = useAppSelector(selectSettings);
    const currentModule = useAppSelector(s => selectModule(s).currentModule);

    const devicesSlice = useAppSelector(selectDevices);
    const deviceTypesSlice = useAppSelector(selectDeviceTypes);
    const rows = useAppSelector(selectAllDevices);
    const selectedDevice = useAppSelector(state => value ? selectDeviceById(state, value) : undefined);
    const selectedDeviceType = useAppSelector(state => selectedDevice?.structureId ? selectDeviceTypeById(state, selectedDevice.structureId) : undefined);

    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState<GridRowSelectionModel>(value ? [value] : []);

    const selectedTypeName = selectedDeviceType?.name ?? "";

    useEffect(() => {
        setSelection(value ? [value] : []);
    }, [value]);

    useEffect(() => {
        if (!value || selectedDevice) {
            return;
        }

        dispatch(loadDeviceDetails(String(value)));
    }, [dispatch, selectedDevice, value]);

    useEffect(() => {
        if (!selectedDevice?.structureId || deviceTypesSlice.state.type !== "empty") {
            return;
        }

        dispatch(loadDeviceTypes());
    }, [deviceTypesSlice.state.type, dispatch, selectedDevice?.structureId]);


    useEffect(() => {
        if (!open) return;
        if (devicesSlice.state.type === "empty" && currentModule) {
            dispatch(loadDevices(currentModule.sysModuleId));
        }
        if (deviceTypesSlice.state.type === "empty") {
            dispatch(loadDeviceTypes());
        }
    }, [open, dispatch, devicesSlice.state.type, deviceTypesSlice.state.type, currentModule]);

    const localeText = useMemo(() => {
        const en = enUS.components.MuiDataGrid.defaultProps.localeText;
        return locale === "pl" ? plPL.components.MuiDataGrid.defaultProps.localeText : en;
    }, [locale]);

    const columns = useMemo<GridColDef[]>(
        () => [
            { field: "name", headerName: "Nazwa", flex: 1, minWidth: 180 },
            {
                field: "structureId",
                headerName: "Typ",
                flex: 1,
                minWidth: 140,
                valueGetter: (_, row) => {
                    const entity = selectDeviceTypeById(store.getState(), row.structureId);
                    return entity?.name ?? "";
                },
            },
            {
                field: "manufacturer",
                headerName: "Producent",
                width: 200
            },
            { field: "model", headerName: "Model", width: 160 },
            { field: "serialNo", headerName: "Nr fabryczny", minWidth: 160, flex: 0.8 },
        ],
        []
    );

    const findDeviceById = (id: string | number) =>
        rows.find(r => String(r.id) === String(id));

    const confirmSelection = () => {
        const id = selection.length ? String(selection[0]) : "";
        if (id) {
            onChange(id);
            onSelectDevice?.(findDeviceById(id));
        }
        setOpen(false);
    };

    const clearSelection = () => {
        setSelection([]);
        onChange("");
        onSelectDevice?.(undefined);
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        setOpen(false);
    };

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                {value ? (
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                ) : (
                    <PriorityHighIcon sx={{ color: "error.main", fontSize: 18 }} aria-hidden />
                )}

                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: value ? "text.secondary" : "error.main" }}
                >
                    {value ? "Wybrane urządzenie" : "Wybierz urządzenie"}
                </Typography>
            </Stack>
            <Paper
                variant="outlined"
                onClick={() => !disabled && setOpen(true)}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (disabled) {
                        return;
                    }
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.75 : 1,
                    borderColor: theme.palette.divider,
                    "&:hover": { borderColor: disabled ? theme.palette.divider : theme.palette.text.primary },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                })}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">

                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Nazwa: {selectedDevice?.name ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            S/N: {selectedDevice?.serialNo ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Struktura: {selectedTypeName || "—"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value && !disabled && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearSelection(); }}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                        {!disabled && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </Paper>
            <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>Wybierz urządzenie</DialogTitle>
                <DialogContent dividers>
                    {devicesSlice.state.type !== "loaded" ? (
                        <Skeleton variant="rectangular" height="60vh" />
                    ) : (
                        <Box sx={{ height: '60vh', minHeight: 300, width: '100%' }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                localeText={localeText}
                                density="compact"
                                rowSelectionModel={selection}
                                onRowSelectionModelChange={(model) => {
                                    setSelection(model.length ? [String(model[0])] : []);
                                }}
                                slots={{ toolbar: QuickSearchToolbar }}
                                initialState={{
                                    sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={confirmSelection} disabled={!selection.length}>
                        Wybierz
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
